import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { X } from 'lucide-react';

import { useAllExpenseApi } from './config/useAllExpenseApi';
import { parseTableParams } from '../../utils/Utils';
import { updatePaymentStatus } from '../../services/expenseService';
import DataTable from '../../components/ui/DataTable';
import OptionsMenu from '../../components/ui/OptionsMenu';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { requiresPayableConfirmation, describeSavedExpensePolicy, formatPolicyAmount } from './config/expensePolicyHelpers';

const Container = styled.div``;

// Projeler sayfasından gelen süzgeç — tablonun üstünde, kaldırılabilir bir çip.
const FilterChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  padding: 6px 10px 6px 12px;
  border-radius: 16px;
  background: #0291ef15;
  color: #0277bd;
  font-size: 13px;
  font-weight: 600;
`;

const ChipClose = styled.button`
  display: grid;
  place-items: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: inherit;
  cursor: pointer;
  &:hover { background: #0291ef25; }
`;

/// ?projectId=…&projectName=… → { id, name } | null
export const readProjectFilter = (search) => {
  const p = new URLSearchParams(search || '');
  const id = p.get('projectId');
  return id ? { id, name: p.get('projectName') || '' } : null;
};

export default function AllExpensesPage() {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();

  const [queryParams, setQueryParams] = useState({ pageSize: 10, pageNumber: 1 });
  const [filterList, setFilterList] = useState([]);
  const [projectFilter, setProjectFilter] = useState(() => readProjectFilter(location?.search));

  const effectiveParams = useMemo(
    () => (projectFilter ? { ...queryParams, 'projectId[eq]': projectFilter.id } : queryParams),
    [queryParams, projectFilter],
  );

  const { columns: baseColumns, data, fetching, pagination, refresh } = useAllExpenseApi(effectiveParams);

  const clearProjectFilter = useCallback(() => {
    setProjectFilter(null);
    history.replace('/all-expenses');
  }, [history]);

  const handleDetails = useCallback((row) => {
    history.push(`/expenses/${row.id}`);
  }, [history]);

  // Kırpılmış talepte ödenecek tutar, ödendi işaretlenmeden ÖNCE gösterilir —
  // ödemeyi yapan kişi limit üstü kısmı yanlışlıkla ödemesin. İhlalsiz talepler
  // tek tıkla işaretlenmeye devam eder.
  const [payConfirm, setPayConfirm] = useState(null);
  const [paying, setPaying] = useState(false);

  const applyPayment = useCallback(async (row, next) => {
    try {
      await updatePaymentStatus(row.id, next);
      refresh?.();
    } catch { /* interceptor */ }
  }, [refresh]);

  const handleSetPayment = useCallback(async (row, next) => {
    if (requiresPayableConfirmation(row, next)) {
      setPayConfirm({ row, next });
      return;
    }
    await applyPayment(row, next);
  }, [applyPayment]);

  const handlePayConfirm = useCallback(async () => {
    if (!payConfirm) return;
    setPaying(true);
    await applyPayment(payConfirm.row, payConfirm.next);
    setPaying(false);
    setPayConfirm(null);
  }, [applyPayment, payConfirm]);

  const payConfirmMessage = useMemo(() => {
    if (!payConfirm) return '';
    const policy = describeSavedExpensePolicy(payConfirm.row);
    const currency = payConfirm.row?.currency || 'TRY';
    return [
      t('expense.policy.paymentPayable', { amount: formatPolicyAmount(policy?.payableAmount, currency) }),
      t('expense.policy.notPayable', { amount: formatPolicyAmount(policy?.exceededAmount, currency) }),
    ].join(' ');
  }, [payConfirm, t]);

  const handleRowClick = useCallback((row) => {
    
    history.push(`/expenses/${row.id}`);
  }, [history]);

  const handleDataStateChange = useCallback((action, tableState) => {
    setFilterList(tableState.filterList);
    const params = parseTableParams(tableState, baseColumns);
    setQueryParams(params);
  }, [baseColumns]);

  const handleFilterConfirm = useCallback((newFilterList) => {
    setFilterList(newFilterList);
  }, []);

  const actionOptions = useMemo(() => [
    { type: 'details', label: t('expense.detail'), onClick: handleDetails },
    {
      type: 'markAsPaid',
      label: t('expense.markAsPaid'),
      onClick: (row) => handleSetPayment(row, 'paid'),
      disabled: (row) => row?.paymentStatus === 'paid',
    },
    {
      type: 'markAsUnpaid',
      label: t('expense.markAsUnpaid'),
      onClick: (row) => handleSetPayment(row, 'unpaid'),
      disabled: (row) => row?.paymentStatus !== 'paid',
    },
  ], [t, handleDetails, handleSetPayment]);

  const columns = useMemo(() => [
    ...baseColumns,
    {
      name: 'actions',
      label: ' ',
      options: {
        filter: false, sort: false, download: false, print: false,
        customBodyRender: (_, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          return <OptionsMenu actions={actionOptions} row={row} />;
        },
      },
    },
  ], [baseColumns, actionOptions, data]);

  return (
    <Container>
      <ConfirmDialog
        isOpen={Boolean(payConfirm)}
        title={t('expense.policy.savedTitle')}
        message={payConfirmMessage}
        onConfirm={handlePayConfirm}
        onCancel={() => setPayConfirm(null)}
        confirmText={t('expense.markAsPaid')}
        loading={paying}
      />

      {projectFilter && (
        <FilterChip data-testid="project-filter-chip">
          {t('expense.projectFilter', { name: projectFilter.name || '—' })}
          <ChipClose type="button" onClick={clearProjectFilter} aria-label={t('expense.clearProjectFilter')} title={t('expense.clearProjectFilter')}>
            <X size={13} />
          </ChipClose>
        </FilterChip>
      )}

      {useMemo(() => (
        <DataTable
          title={t('expense.allExpenses')}
          fetching={fetching}
          columns={columns}
          data={data}
          totalRowCount={pagination.totalRowCount}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          filterList={filterList}
          onFilterConfirm={handleFilterConfirm}
          onTableChanged={handleDataStateChange}
          onRowClick={handleRowClick}
        />
      ), [
        t, fetching, columns, data, pagination,
        filterList, handleFilterConfirm, handleDataStateChange, handleRowClick,
      ])}
    </Container>
  );
}
